import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../config";
import { useBookingModal } from "../context/BookingModalContext";

const Hero = () => {
  const { openBookingModal } = useBookingModal();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Mobile bar hire setup at an outdoor event"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#fafafa]/90 via-[#fafafa]/70 to-[#fafafa]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-12 pt-24 md:pt-32 pb-16 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-[#444444] font-script text-xl md:text-2xl lg:text-3xl mb-2 md:mb-4"
          >
            Premium Mobile Bar Hire
          </motion.p>

          <h1 id="hero-heading" className="text-4xl md:text-5xl lg:text-7xl leading-tight md:leading-none mb-4 md:mb-8 text-[#a5dae0]">
            Mobile Bar Hire
            <span className="block mt-1 md:mt-2 text-[#444444]">For Every Occasion</span>
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-[#444444] max-w-2xl mx-auto mb-8 md:mb-12 leading-relaxed px-2">
            Award-winning mobile bar hire for weddings, corporate events and festivals.
            Based in Birmingham and Cambridge, serving across the UK.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={openBookingModal}
              data-testid="hero-book-now"
              className="w-full sm:w-auto bg-[#a5dae0] text-[#1a1a1a] px-8 md:px-10 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2"
            >
              Get Your Quote
              <ArrowRight size={18} aria-hidden="true" />
            </motion.button>
            <Link
              to="/menu"
              data-testid="hero-view-menu"
              className="w-full sm:w-auto border-2 border-[#444444] text-[#444444] px-8 md:px-10 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:border-[#a5dae0] hover:bg-[#a5dae0] hover:text-[#1a1a1a] transition-all text-center focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2"
            >
              View Our Menu
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          role="list"
          aria-label="Business statistics"
        >
          {[
            { number: "300+", label: "Events Served" },
            { number: "100K+", label: "Drinks Poured" },
            { number: "80+", label: "Weddings" },
            { number: "5★", label: "Rated Service" },
          ].map((stat, i) => (
            <div key={i} className="text-center" role="listitem">
              <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a]">{stat.number}</p>
              <p className="text-[#444444] mt-1 md:mt-2 text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-[#444444]/40 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-1.5 h-3 bg-[#a5dae0] rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
